import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { ArrowRight, Check, Menu, X, Zap } from "lucide-react";

import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { logoUrl } from "@/lib/logo-version";
import { cn } from "@/lib/utils";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { DeferUntilVisible } from "@/components/DeferUntilVisible";

const importHomeSections = () => import("@/components/home/HomeSections");

const HomeSections = lazy(() =>
  importHomeSections().then((m) => ({ default: m.HomeSections })),
);
const HomeFooter = lazy(() =>
  importHomeSections().then((m) => ({ default: m.HomeFooter })),
);
const HeroPreview = lazy(() =>
  import("@/components/home/HeroPreview").then((m) => ({ default: m.HeroPreview })),
);
const RoleSelectorDialog = lazy(() =>
  import("@/components/home/RoleSelectorDialog").then((m) => ({
    default: m.RoleSelectorDialog,
  })),
);

import { FreePlanSlotsBadge } from "@/components/FreePlanSlotsBadge";
import { useIsMobile } from "@/hooks/use-mobile";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Lumvio — Client Portal & Payment Protection for Freelancers" },
      {
        name: "description",
        content:
          "Lumvio is the client portal for freelancers: send invoices, hold funds under payment protection, and release payment when the work is approved. Identity-verified accounts only.",
      },
      {
        name: "keywords",
        content:
          "client portal for freelancers, freelance payment protection, protected milestone payments, verified freelancers, secure freelance invoicing, freelance project workspace, freelancer payouts, identity-verified freelancers, get paid as a freelancer, freelance contract and invoice platform",
      },
      {
        property: "og:title",
        content: "Lumvio — Client Portal & Payment Protection for Freelancers",
      },
      {
        property: "og:description",
        content:
          "A client portal for freelancers and clients. Verified identities, protected payments, one clean workspace.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://lumvio.co/" },
      { property: "og:site_name", content: "Lumvio" },
      {
        property: "og:image",
        content:
          "https://lumvio.co/__l5e/assets-v1/bbd15e4b-2805-4707-8582-06a259cb86cc/lumvio-og-v3.png",
      },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Lumvio — Verified freelancer workspace" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Lumvio — Client portal & payment protection" },
      {
        name: "twitter:description",
        content: "Verified freelancers. Protected payments. One clean client portal.",
      },
      {
        name: "twitter:image",
        content:
          "https://lumvio.co/__l5e/assets-v1/bbd15e4b-2805-4707-8582-06a259cb86cc/lumvio-og-v3.png",
      },
    ],
    links: [
      { rel: "canonical", href: "https://lumvio.co/" },
      { rel: "alternate", hrefLang: "en", href: "https://lumvio.co/" },
      { rel: "alternate", hrefLang: "ar", href: "https://lumvio.co/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://lumvio.co/" },
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Lumvio",
          url: "https://lumvio.co/",
          logo: "https://lumvio.co/favicon.svg",
          description:
            "Lumvio is the identity-verified client portal for freelancers — Payment Protection on every milestone, invoices, client chat and payouts in one workspace.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Lumvio",
          url: "https://lumvio.co/",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://lumvio.co/?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              q: "What makes Lumvio different from other freelance platforms?",
              a: "Every freelancer is identity-verified through Stripe Identity before they can list work, which helps keep fake and ghost accounts off the platform.",
            },
            {
              q: "How does identity verification work?",
              a: "You upload a government-issued ID and take a quick live selfie. Stripe's biometric engine confirms the match in seconds, then your profile earns a permanent verified badge.",
            },
            {
              q: "How do payments and payment protection work?",
              a: "Clients fund an invoice before work begins. Funds are held under payment protection until the deliverable is approved, then the freelancer can withdraw via Stripe or crypto.",
            },
            {
              q: "Is there a free plan?",
              a: "Yes. Complete identity verification and unlock one free project to try the platform. Upgrade anytime when you're ready for more volume.",
            },
            {
              q: "What happens if a client doesn't release payment?",
              a: "Payment protection covers both sides. If a client goes silent, funds auto-release to the freelancer after the review window ends.",
            },
          ].map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Lumvio",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          description:
            "Client portal for freelancers: invoices, Payment Protection on milestones, identity-verified accounts and secure payouts.",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
        }),
      },
    ],
  }),
  component: Landing,
});

export function Landing({ scrollTo }: { scrollTo?: string } = {}) {
  const { t, i18n } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const isMobile = useIsMobile();
  const [roleOpen, setRoleOpen] = useState(false);
  const trustItems = t("home.trustItems", { returnObjects: true }) as string[];
  const isRTL = i18n.dir() === "rtl";
  const [freeSlots, setFreeSlots] = useState<{
    claimed: number;
    total: number;
    remaining: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getFreePlanSlots } = await import("@/lib/site-stats.functions");
        const s = await getFreePlanSlots();
        if (!cancelled) setFreeSlots(s);
      } catch {
        /* noop */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/dashboard" });
  }, [user, loading, navigate]);

  // Warm the below-the-fold chunk right after first paint so fast scrolling
  // never lands on an unmounted (blank) region while the chunk downloads.
  useEffect(() => {
    let cancelled = false;
    const warm = () => {
      if (!cancelled) void importHomeSections();
    };
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    }).requestIdleCallback;
    const id = ric ? ric(warm, { timeout: 1200 }) : window.setTimeout(warm, 300);
    window.addEventListener("scroll", warm, { passive: true, once: true });
    return () => {
      cancelled = true;
      window.removeEventListener("scroll", warm);
      if (!ric) window.clearTimeout(id as number);
    };
  }, []);



  useEffect(() => {
    if (!scrollTo) return;
    const id = scrollTo;
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    // Wait for layout
    const t1 = setTimeout(scroll, 50);
    const t2 = setTimeout(scroll, 400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [scrollTo]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Close mobile menu if open
    setMobileMenuOpen(false);
  };
  const scrollToPricing = () => scrollToSection("pricing");

  const heroRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  // (Hero text parallax removed: it forced the LCP headline to wait for
  // framer-motion hydration. Decorative motion now lives below the fold.)

  // Global page scroll: progress rail + nav scrolled state.
  // Plain passive listener + direct style write (no framer-motion on the
  // critical path, no re-render per scroll frame).
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [roleMounted, setRoleMounted] = useState(false);
  const openRole = () => {
    setRoleMounted(true);
    setRoleOpen(true);
  };
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const y = window.scrollY;
      const pct = max > 0 ? Math.max(0, Math.min(1, y / max)) * 100 : 0;
      if (railRef.current) railRef.current.style.width = `${pct}%`;
      setNavScrolled(y > 24);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div className="min-h-dvh w-full overflow-x-clip bg-[#050505] text-zinc-100 selection:bg-indigo-500/30 font-sans">
      {/*
        Global ambient layer. CSS-gated (not JS-gated) so the SSR markup and the
        first client frame agree — no flash of desktop gradients on phones.
        Two blurs instead of four, and a single grid: violet identity only.
      */}
      <div className="pointer-events-none fixed inset-0 -z-10 hidden lg:block">
        <div className="absolute left-1/2 top-0 h-[600px] w-[900px] -translate-x-1/2 bg-gradient-to-b from-indigo-500/15 via-purple-500/5 to-transparent blur-3xl" />
        <div className="absolute right-0 top-1/3 h-96 w-96 bg-violet-600/10 blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      {/* Top scroll progress rail — one brand gradient, one soft head */}
      <div className="pointer-events-none fixed inset-x-0 top-0 z-50 h-[2px] bg-white/[0.04]">
        <div
          ref={railRef}
          className="absolute inset-y-0 h-full w-0"
          style={isRTL ? { right: 0 } : { left: 0 }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-violet-400 to-fuchsia-400 rtl:bg-gradient-to-l" />
          <div
            className={cn(
              "absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-white/90 shadow-[0_0_10px_2px_rgba(167,139,250,0.6)]",
              isRTL ? "left-0" : "right-0",
            )}
          />
        </div>
      </div>


      <div className="relative mx-auto w-full max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
        {/* Nav */}
        <nav
          aria-label="Primary"
          className={cn(
            "nav-drop sticky top-4 z-30 mx-auto flex items-center justify-between gap-3 rounded-2xl border transition-all duration-500",
            navScrolled
              ? "border-white/10 bg-black/75 px-3 py-2 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-2xl"
              : "border-white/[0.08] bg-white/[0.025] px-3.5 py-2.5 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-xl",
          )}
        >
          {/* aurora hairline sheen */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-16 -bottom-px h-px bg-gradient-to-r from-transparent via-violet-400/25 to-transparent"
          />

          <Link
            to="/"
            onClick={() => {
              if (typeof window !== "undefined" && window.location.pathname === "/")
                window.location.reload();
            }}
            className="group/logo relative flex items-center gap-2.5 ps-1"
          >
            <div className="relative">
              {/* animated conic ring on hover */}
              <span
                aria-hidden
                className="pointer-events-none absolute -inset-[2px] rounded-[12px] opacity-0 blur-[6px] transition-opacity duration-500 group-hover/logo:opacity-100"
                style={{
                  background:
                    "conic-gradient(from 0deg, rgba(99,102,241,0.6), rgba(217,70,239,0.5), rgba(45,212,191,0.5), rgba(99,102,241,0.6))",
                }}
              />
              <div className="relative h-9 w-9 overflow-hidden rounded-[10px] bg-gradient-to-br from-white/[0.10] to-white/[0.02] ring-1 ring-white/10 transition-all duration-300 group-hover/logo:ring-white/25">
                <img
                  src={logoUrl()}
                  alt="Lumvio logo"
                  width={36}
                  height={36}
                  decoding="async"
                  fetchPriority="high"
                  className="h-full w-full object-contain p-1"
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    img.style.display = "none";
                    const fallback = img.nextElementSibling as HTMLElement | null;
                    if (fallback) fallback.style.display = "block";
                  }}
                />
                <Zap
                  className="pointer-events-none absolute inset-0 m-auto h-4 w-4 text-white hidden"
                  strokeWidth={2.5}
                />
              </div>
            </div>
            <span
              className="text-[13px] sm:text-[15px] font-semibold tracking-tight text-white"
              translate="no"
            >
              Lumvio
            </span>
            <span className="ms-1 hidden items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/[0.10] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-emerald-300 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.05)] md:inline-flex">
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
              {t("home.preview.live", { defaultValue: "Live" })}
            </span>
          </Link>

          {/* Center nav — segmented pill */}
          <div className="pointer-events-auto absolute left-1/2 hidden -translate-x-1/2 items-center gap-0.5 rounded-full border border-white/[0.06] bg-white/[0.02] p-1 backdrop-blur-md md:inline-flex">
            <a
              href="#features"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("features");
              }}
              className="relative rounded-full px-4 py-1.5 text-[13px] font-medium text-zinc-400 transition-all duration-300 hover:text-white hover:bg-white/[0.06]"
            >
              {t("nav.features")}
            </a>
            <a
              href="#pricing"
              onClick={(e) => {
                e.preventDefault();
                scrollToPricing();
              }}
              className="relative rounded-full px-4 py-1.5 text-[13px] font-medium text-zinc-400 transition-all duration-300 hover:text-white hover:bg-white/[0.06]"
            >
              {t("nav.pricing")}
            </a>
          </div>

          <div className="flex min-w-0 items-center gap-2">
            <div className="hidden rounded-full border border-white/[0.06] bg-white/[0.02] px-1 py-0.5 text-zinc-400 sm:block">
              <LanguageSwitcher />
            </div>
            <Link
              to="/auth"
              className="hidden whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-medium text-zinc-300 transition-colors hover:text-white sm:inline-flex"
            >
              {t("common.signIn")}
            </Link>
            <div className="group/cta relative">
              {/* aurora glow behind CTA */}
              <span
                aria-hidden
                className="pointer-events-none absolute -inset-[3px] rounded-full opacity-60 blur-md transition-opacity duration-500 group-hover/cta:opacity-100"
                style={{
                  background:
                    "conic-gradient(from 180deg, rgba(99,102,241,0.45), rgba(217,70,239,0.4), rgba(45,212,191,0.35), rgba(99,102,241,0.45))",
                }}
              />
              <button
                type="button"
                onClick={openRole}
                className="relative inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-4 py-1.5 sm:px-[18px] sm:py-2 text-[12px] sm:text-[13px] font-semibold text-black transition-all duration-300 hover:shadow-[0_0_0_4px_rgba(255,255,255,0.10),0_14px_40px_-10px_rgba(165,180,252,0.6)] active:scale-[0.98]"
                aria-label={t("common.getStarted") ?? "Get started"}
              >
                <span>{t("common.getStarted")}</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/cta:translate-x-0.5 rtl:rotate-180 rtl:group-hover/cta:-translate-x-0.5" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Menu"
              aria-expanded={mobileMenuOpen}
              className="-me-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-zinc-200 transition-colors hover:bg-white/[0.08] sm:hidden"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {/* Mobile menu panel */}
          {mobileMenuOpen && (
            <div className="menu-drop absolute inset-x-2 top-full mt-2 rounded-2xl border border-white/10 bg-black/90 p-3 shadow-2xl backdrop-blur-xl sm:hidden">
              <div className="flex flex-col gap-1">
                <a
                  href="#features"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("features");
                  }}
                  className="flex min-h-11 items-center rounded-lg px-3 py-2.5 text-sm font-medium text-zinc-200 hover:bg-white/5"
                >
                  {t("nav.features")}
                </a>
                <a
                  href="#pricing"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToPricing();
                  }}
                  className="flex min-h-11 items-center rounded-lg px-3 py-2.5 text-sm font-medium text-zinc-200 hover:bg-white/5"
                >
                  {t("nav.pricing")}
                </a>

                <Link
                  to="/auth"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex min-h-11 items-center rounded-lg px-3 py-2.5 text-sm font-medium text-zinc-200 hover:bg-white/5"
                >
                  {t("common.signIn")}
                </Link>
                <div className="mt-1 flex min-h-11 flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-zinc-300">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">Language</span>
                  <LanguageSwitcher onChange={() => setMobileMenuOpen(false)} />
                </div>
              </div>
            </div>
          )}
        </nav>

        {/* Hero */}
        <main id="main">
          <section
            ref={heroRef}
            className="relative pt-14 pb-16 text-center lg:pt-20 lg:pb-20"
            aria-labelledby="hero-heading"
          >
            {/* One violet floor glow — the second fuchsia wash was invisible
                under this one and cost a full-viewport blur. */}
            <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
              <div className="absolute bottom-[-10%] left-1/2 h-[720px] w-[1100px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(124,58,237,0.28),rgba(139,92,246,0.12)_45%,transparent_70%)] blur-[120px]" />
            </div>

            {/*
              Above-the-fold hero: rendered as plain SSR markup with CSS-only
              entrance animation. The <h1> (the LCP element) carries no
              opacity/transform/delay so it paints on the very first frame,
              independent of hydration.
            */}
            <div>
              <div className="hero-rise-1 mb-7 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70 backdrop-blur-md rtl:tracking-normal">
                <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                {t("home.badge")}
              </div>

              <div id="hero-heading">
                <h1
                  className="mx-auto mb-6 max-w-4xl text-[2.75rem] font-extrabold leading-[1.08] tracking-tight rtl:leading-[1.22] rtl:tracking-normal sm:text-5xl lg:text-[5.25rem]"
                  aria-label={`${t("home.heroTitlePrefix")} ${t("home.heroTitleEmphasis")}`}
                >
                  <span className="bg-gradient-to-b from-white via-white to-white/80 bg-clip-text text-transparent">
                    {t("home.heroTitlePrefix")}{" "}
                  </span>
                  <span className="bg-gradient-to-r from-indigo-200 via-violet-300 to-fuchsia-300 bg-clip-text text-transparent drop-shadow-[0_0_42px_rgba(139,92,246,0.45)]">
                    {t("home.heroTitleEmphasis")}
                  </span>
                </h1>
              </div>

              <p className="hero-rise-2 mx-auto mb-10 max-w-2xl text-base leading-relaxed text-zinc-300 sm:text-lg">
                {t("home.heroSubtitlePrefix")}{" "}
                <span className="text-white font-medium">{t("home.heroSubtitleEmphasis")}</span>{" "}
                {t("home.heroSubtitleSuffix")}
              </p>

              {/* One button, one link — the secondary CTA no longer competes. */}
              <div className="hero-rise-3 mx-auto flex w-full max-w-xs flex-col items-center justify-center gap-4 sm:max-w-none sm:flex-row sm:gap-6">
                <button
                  type="button"
                  onClick={openRole}
                  className="group relative inline-flex min-h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-black shadow-[0_0_40px_-8px_rgba(255,255,255,0.35)] transition-all hover:scale-[1.02] active:scale-[0.98] sm:w-auto"
                >
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-black/10 to-transparent transition-transform duration-700 group-hover:translate-x-[400%]"
                  />
                  {freeSlots
                    ? t("home.heroCtaSeats", {
                        defaultValue: "Claim your free seat — {{count}} left",
                        count: freeSlots.remaining,
                      })
                    : t("home.heroCtaSeatsPlain", { defaultValue: "Claim your free seat" })}
                  <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5" />
                </button>
                <a
                  href="#how-it-works"
                  className="group/link inline-flex min-h-11 items-center justify-center gap-1.5 text-sm font-medium text-zinc-300 transition-colors hover:text-white"
                >
                  <span className="underline decoration-white/25 underline-offset-4 hover:decoration-white/60">
                    {t("nav.howItWorks")}
                  </span>
                </a>
              </div>

              {freeSlots ? (
                <div className="hero-rise mt-5 flex justify-center">
                  <FreePlanSlotsBadge slots={freeSlots} variant="pill" />
                </div>
              ) : null}
            </div>

            {/* Hero preview: the product does the selling, so it comes before
                the trust chips now. */}
            <div className="perspective-[1200px] relative">
              <Suspense
                fallback={<div className="mt-14 h-[560px] sm:h-[620px] lg:mt-20" aria-hidden />}
              >
                <HeroPreview />
              </Suspense>
            </div>

            {/* Proof strip — moved below the product preview. Two-up on narrow
                screens so it stops wrapping into a ragged three-line block. */}
            <div className="hero-rise-4 mx-auto mt-10 grid max-w-3xl grid-cols-2 gap-2 rtl:tracking-normal sm:flex sm:flex-wrap sm:items-center sm:justify-center">
              {trustItems.map((item) => (
                <span
                  key={item}
                  className="group inline-flex items-center justify-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] font-medium text-zinc-400 backdrop-blur-sm transition-all hover:border-white/20 hover:bg-white/[0.06] hover:text-zinc-100"
                >
                  <Check className="h-3 w-3 shrink-0 text-emerald-400" />
                  {item}
                </span>
              ))}
            </div>


            {roleMounted ? (
              <Suspense fallback={null}>
                <RoleSelectorDialog open={roleOpen} onOpenChange={setRoleOpen} />
              </Suspense>
            ) : null}
          </section>

          {/* Everything below the fold is one lazily-loaded chunk so the
              homepage critical bundle stays small on mobile. */}
          <DeferUntilVisible minHeight={900} rootMargin="2400px">
            <Suspense fallback={<div className="min-h-[900px]" aria-hidden />}>
              <HomeSections freeSlots={freeSlots} />
            </Suspense>
          </DeferUntilVisible>
        </main>

        <DeferUntilVisible minHeight={420} rootMargin="2400px">
          <Suspense fallback={<div className="min-h-[420px]" aria-hidden />}>
            <HomeFooter />
          </Suspense>
        </DeferUntilVisible>
      </div>
    </div>
  );
}

/* ============================================================ */
/* HERO — Floating decorative orbs                                */
/* ============================================================ */

/* ============================================================ */
/* SECURITY GATE — 3 animated cards                               */
/* ============================================================ */

/* Step 1 — Neon laser scanning an ID */

/* Step 2 — Biometric face grid */

/* ============================================================ */
/* CRYPTO PAYMENTS SECTION                                      */
/* ============================================================ */

/* Step 3 — Verified badge reveal */

/* ============================================================ */
/* TRUSTED BY CREATORS                                          */
/* ============================================================ */

/* ============================================================ */
/* FAQ                                                            */
/* ============================================================ */

/* ============================================================ */


/* ============================================================ */
/* Animated Counter                                               */
/* ============================================================ */

/* ============================================================ */
/* Shared helpers                                                 */
/* ============================================================ */

/* ============ BENTO FEATURE CARDS ============ */

/* ============================================================ */
/* HERO PREVIEW — Ethereal product spotlight dashboard mockup     */
/* ============================================================ */

/* ============================================================ */
/* TRUSTED BY — Logo bar                                        */
/* ============================================================ */

/* ============================================================ */
/* HOW IT WORKS — 3 scroll-triggered value steps                */
/* ============================================================ */

/* ============================================================ */
/* ROLE SELECTOR — quick onboarding path split                  */
/* ============================================================ */
